
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export const useBlockDate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const blockDateMutation = useMutation({
    mutationFn: async ({ propertyId, date }: { propertyId: string; date: Date }) => {
      const dateString = format(date, 'yyyy-MM-dd');
      
      console.log('🔒 Attempting to block date:', { propertyId, dateString });
    
      const { data, error } = await supabase
        .from('property_blocked_days')
        .insert({
          property_id: propertyId,
          blocked_date: dateString,
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error blocking date:', error);
        console.error('Error details:', { 
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      console.log('✅ Successfully blocked date:', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('🎉 Block date mutation success:', data);
      queryClient.invalidateQueries({ queryKey: ['batched-availability'] });
      queryClient.invalidateQueries({ queryKey: ['manually-blocked-dates'] });
      toast({
        title: "Fecha bloqueada",
        description: "El día ha sido bloqueado exitosamente",
      });
    },
    onError: (error: any) => {
      console.error('💥 Block date mutation error:', error);
      toast({
        title: "Error",
        description: `Error al bloquear la fecha: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const unblockDateMutation = useMutation({
    mutationFn: async ({ propertyId, date }: { propertyId: string; date: Date }) => {
      const dateString = format(date, 'yyyy-MM-dd');
      
      const { error } = await supabase
        .from('property_blocked_days')
        .delete()
        .eq('property_id', propertyId)
        .eq('blocked_date', dateString);

      if (error) {
        console.error('Error unblocking date:', error);
        throw error;
      }
    },
    onSuccess: () => {
      console.log('🎉 Unblock date mutation success');
      queryClient.invalidateQueries({ queryKey: ['batched-availability'] });
      queryClient.invalidateQueries({ queryKey: ['manually-blocked-dates'] });
      toast({
        title: "Fecha desbloqueada",
        description: "El día ha sido desbloqueado exitosamente",
      });
    },
    onError: (error: any) => {
      console.error('💥 Unblock date mutation error:', error);
      toast({
        title: "Error",
        description: `Error al desbloquear la fecha: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    blockDate: blockDateMutation.mutate,
    unblockDate: unblockDateMutation.mutate,
    isBlocking: blockDateMutation.isPending,
    isUnblocking: unblockDateMutation.isPending,
  };
};
